import {
  Building2,
  Clock3,
  CreditCard,
  Home,
  MapPin,
  Package,
  Phone,
} from 'lucide-react';
import AddressEditor from '../../../../common/addressSchedule/AddressEditor.tsx';
import WeeklyScheduleEditor from '../../../../common/addressSchedule/WeeklyScheduleEditor.tsx';
import {
  BooleanToggle,
  Chip,
  Field,
  SectionCard,
  inputBase,
} from './FormPromptControls.tsx';

type RestaurantStepProps = {
  form: any;
  errors: Record<string, string>;
  draftFieldKeys: Set<string>;
  updateField: (key: any, value: any) => void;
  showEnderecoTab: boolean;
  showEndereco: boolean;
  showCidadeEstado: boolean;
  showPresencial: boolean;
  showHorarios: boolean;
  showLinks: boolean;
  enderecoObrigatorio: boolean;
  modoPedidos: 'whatsapp' | 'link' | null;
  enderecoBusca: string;
  buscandoEndereco: boolean;
  enderecoResultados: any[];
  setEnderecoBusca: (value: string) => void;
  setEnderecoResultados: (value: any[]) => void;
  handleBuscarEndereco: () => void;
  handleSelecionarEndereco: (item: any) => void;
  mapPreviewUrl: string | null;
  UF_OPTIONS: string[];
  CITY_OPTIONS_BY_UF: Record<string, string[]>;
  horariosOpen: boolean;
  setHorariosOpen: (open: boolean) => void;
  openingHoursSummary: string;
  toggleDayClosed: (...args: any[]) => void;
  addPeriod: (...args: any[]) => void;
  updatePeriod: (...args: any[]) => void;
  removePeriod: (...args: any[]) => void;
  replicateScheduleToAll: (...args: any[]) => void;
  addSpecialDate: () => void;
  removeSpecialDate: (index: number) => void;
  updateSpecialDate: (...args: any[]) => void;
  addSpecialPeriod: (index: number) => void;
  removeSpecialPeriod: (index: number, periodIndex: number) => void;
  updateSpecialPeriod: (...args: any[]) => void;
  PAYMENT_OPTIONS: string[];
  toggleArrayValue: (key: string, value: string) => void;
  bandeiraInput: string;
  setBandeiraInput: (value: string) => void;
  addBandeira: () => void;
  removeBandeira: (item: string) => void;
  showLinksAdvanced: boolean;
  setShowLinksAdvanced: (value: boolean | ((prev: boolean) => boolean)) => void;
  eventosOn: boolean;
  encomendasOn: boolean;
  eventosDirty: boolean;
  encomendasDirty: boolean;
  setAtendimentoType: (key: string, value: boolean) => void;
};

const RestaurantStep = ({
  form,
  errors,
  draftFieldKeys,
  updateField,
  showEnderecoTab,
  showEndereco,
  showCidadeEstado,
  showPresencial,
  showHorarios,
  showLinks,
  enderecoObrigatorio,
  modoPedidos,
  enderecoBusca,
  buscandoEndereco,
  enderecoResultados,
  setEnderecoBusca,
  setEnderecoResultados,
  handleBuscarEndereco,
  handleSelecionarEndereco,
  mapPreviewUrl,
  UF_OPTIONS,
  CITY_OPTIONS_BY_UF,
  horariosOpen,
  setHorariosOpen,
  openingHoursSummary,
  toggleDayClosed,
  addPeriod,
  updatePeriod,
  removePeriod,
  replicateScheduleToAll,
  addSpecialDate,
  removeSpecialDate,
  updateSpecialDate,
  addSpecialPeriod,
  removeSpecialPeriod,
  updateSpecialPeriod,
  PAYMENT_OPTIONS,
  toggleArrayValue,
  bandeiraInput,
  setBandeiraInput,
  addBandeira,
  removeBandeira,
  showLinksAdvanced,
  setShowLinksAdvanced,
  eventosOn,
  encomendasOn,
  eventosDirty,
  encomendasDirty,
  setAtendimentoType,
}: RestaurantStepProps) => (
  <>
<>
            <SectionCard
              title="Informações básicas"
              icon={Building2}
              hint="Como a IA vai chamar e apresentar o seu restaurante para os clientes."
              description="Informações oficiais que a IA pode informar ao cliente."
            >
              <div className="grid gap-4 md:grid-cols-2">
                <Field
                  label="Nome do restaurante"
                  required
                  error={errors.nome_empresa}
                  errorKey="nome_empresa"
                  tooltip="Esse é o nome que a IA usa quando o cliente pergunta 'qual é o nome?'"
                >
                  <input
                    className={inputBase}
                    value={form.nome_empresa}
                    onChange={(e) => updateField('nome_empresa', e.target.value)}
                    placeholder="Ex.: Pizzaria Gourmex"
                  />
                </Field>
                <Field
                  label="Como a IA deve se referir ao restaurante"
                  error={errors.forma_de_chamada}
                  errorKey="forma_de_chamada"
                  tooltip="Isso ajuda a IA a falar do jeito natural do cliente."
                >
                  <div className="flex gap-2">
                    {['vou na', 'vou no', 'vou ao'].map((value) => (
                      <Chip
                        key={value}
                        active={form.forma_de_chamada === value}
                        label={`${value} ${form.nome_empresa || 'seu estabelecimento'}`}
                        onClick={() => updateField('forma_de_chamada', value)}
                      />
                    ))}
                  </div>
                </Field>
              </div>
	              
              <Field
                label="Descrição curta"
                errorKey="historia_da_marca"
                helper="Conte em uma ou duas frases o que torna o restaurante especial."
                tooltip="A IA usa esta descrição ao apresentar o restaurante."
                trailing={(
                  <p className="text-right text-xs text-slate-400">
                    {String(form.historia_da_marca ?? '').length}/300
                  </p>
                )}
              >
                <textarea
                  className={`${inputBase} min-h-[120px]`}
                  value={form.historia_da_marca ?? ''}
                  onChange={(e) => updateField('historia_da_marca', e.target.value)}
                  placeholder="Ex.: Desde 2018, a gente faz pizza artesanal com ingredientes frescos..."
                  maxLength={300}
                />
              </Field>
              
            </SectionCard>

              </>
            
{(showEnderecoTab) && (
<>
                {(showEndereco || showCidadeEstado) && (
                  <SectionCard
                    title="Endereço"
                    icon={MapPin}
                    description="Localização usada para responder onde está o estabelecimento e orientar retirada."
                  >
                      <AddressEditor
                        values={{
                          rua: form.rua,
                          numero: form.numero,
                          bairro: form.bairro,
                          cep: form.cep,
                          cidade: form.cidade,
                          estado: form.estado,
                          endereco_lat: form.endereco_lat,
                          endereco_long: form.endereco_long,
                        }}
                        errors={{
                          rua: errors.rua,
                          numero: errors.numero,
                          bairro: errors.bairro,
                          cep: errors.cep,
                          cidade: errors.cidade,
                          estado: errors.estado,
                          endereco_lat: errors.endereco_lat,
                          endereco_long: errors.endereco_long,
                        }}
                        dirtyFields={{
                          rua: draftFieldKeys.has('rua'),
                          numero: draftFieldKeys.has('numero'),
                          bairro: draftFieldKeys.has('bairro'),
                          cep: draftFieldKeys.has('cep'),
                          cidade: draftFieldKeys.has('cidade'),
                          estado: draftFieldKeys.has('estado'),
                          endereco_lat: draftFieldKeys.has('endereco_lat'),
                          endereco_long: draftFieldKeys.has('endereco_long'),
                        }}
                        inputClassName={inputBase}
                        errorInputClassName="form-prompt-control"
                        searchValue={enderecoBusca}
                        searching={buscandoEndereco}
                        searchResults={enderecoResultados}
                        onSearchValueChange={(value) => {
                          setEnderecoBusca(value);
                          setEnderecoResultados([]);
                        }}
                        onSearch={handleBuscarEndereco}
                        onSelectSearchResult={handleSelecionarEndereco}
                        onFieldChange={(field, value) => updateField(field, value)}
                        showSearch={showEndereco}
                        showCityState={showCidadeEstado}
                        showStreetFields={showEndereco}
                        showGeoFields={showEndereco}
                        geoFieldsInAdvanced
                        cityStateRequired={showCidadeEstado}
                        streetRequired={enderecoObrigatorio}
                        neighborhoodRequired={enderecoObrigatorio}
                        postalCodeRequired={enderecoObrigatorio}
                        ufOptions={UF_OPTIONS}
                        cityOptions={CITY_OPTIONS_BY_UF[form.estado] || []}
                        cityListId="atendabot-cidade-list-tab"
                      />
                      {mapPreviewUrl && (
                        <div className="mt-4 overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
                          <iframe
                            title="Prévia da localização do restaurante"
                            src={mapPreviewUrl}
                            className="h-48 w-full border-0"
                            loading="lazy"
                          />
                          <p className="px-3 py-2 text-xs text-slate-500">Confira se o marcador está na localização correta.</p>
                        </div>
                      )}
                  </SectionCard>
                )}

                {showPresencial && (
                  <SectionCard
                    title="Atendimento presencial"
                    icon={Home}
                    description="Defina se o cliente pode consumir no local ou reservar."
                  >
                    <div className="grid gap-4 md:grid-cols-2">
                      <Field
                        label="Tem atendimento presencial?"
                        required={showPresencial}
                        error={errors.possui_atendimento_presencial}
                        errorKey="possui_atendimento_presencial"
                        tooltip="Marque 'Sim' se o cliente pode consumir no local ou fazer reserva."
                      >
                        <BooleanToggle
                          value={form.possui_atendimento_presencial}
                          onChange={(value) => updateField('possui_atendimento_presencial', value)}
                        />
                      </Field>
                    </div>
                  </SectionCard>
                )}
              </>
            
)}
{(showHorarios) && (
<SectionCard
                title="Horário de funcionamento"
                icon={Clock3}
                description="A IA usa isso para saber quando pode atender. Se tudo estiver fechado, ela não avança com o atendimento."
              >
                  <details
                    className={`rounded-xl border p-4 ${
                      errors.horarios_semana
                        ? 'border-rose-300 bg-rose-50/40'
                        : 'border-slate-200 bg-slate-50'
                    }`}
                    open={horariosOpen}
                    onToggle={(event) => setHorariosOpen((event.target as HTMLDetailsElement).open)}
                  >
                    <summary className={`flex cursor-pointer items-center justify-between gap-4 text-sm ${errors.horarios_semana ? 'text-rose-700' : 'text-slate-700'}`}>
                      <strong>{openingHoursSummary}</strong>
                      <span className={`text-xs font-semibold ${errors.horarios_semana ? 'text-rose-600' : 'text-teal-700'}`}>Editar horários</span>
                    </summary>
                    <div className="mt-5">
                      <WeeklyScheduleEditor
                        days={form.horarios_semana}
                        error={errors.horarios_semana}
                        dirty={draftFieldKeys.has('horarios_semana')}
                        errorKey="horarios_semana"
                        inputClassName={inputBase}
                        errorInputClassName="form-prompt-control"
                        compactTimeInputs
                        onToggleDayClosed={toggleDayClosed}
                        onAddPeriod={addPeriod}
                        onUpdatePeriod={updatePeriod}
                        onRemovePeriod={removePeriod}
                        onReplicateAll={replicateScheduleToAll}
                      />
                    </div>
                  </details>

                  <Field
                    label="Feriados, exceções e datas específicas (opcional)"
                    errorKey="datas_especiais"
                    tooltip="Use para feriados, horários especiais e mudanças temporárias."
                  >
                    <div className="max-w-5xl space-y-4">
                      {form.datas_especiais.length === 0 && (
                        <p className="text-xs text-slate-400">
                          Nenhuma data específica cadastrada.
                        </p>
                      )}
                      {form.datas_especiais.map((item, index) => (
                        <div
                          key={`${item.date}-${index}`}
                          className="space-y-4 rounded-xl border border-slate-200 bg-white p-4"
                        >
                          <div className="flex min-h-[44px] items-center justify-between gap-4">
                            <strong className="text-sm text-slate-800">
                              {item.label?.trim() || `Data específica ${index + 1}`}
                            </strong>
                            <button
                              type="button"
                              onClick={() => removeSpecialDate(index)}
                              className="min-h-[44px] rounded-lg px-2 text-xs font-semibold text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                            >
                              Remover data
                            </button>
                          </div>
                          <div className="grid items-center gap-3 md:grid-cols-[13rem_minmax(0,1fr)_auto]">
                            <input
                              type="date"
                              aria-label={`Data específica ${index + 1}`}
                              className={`${inputBase} min-h-[44px] min-w-0 max-w-full text-base`}
                              value={item.date}
                              onChange={(e) => updateSpecialDate(index, 'date', e.target.value)}
                            />
                            <input
                              aria-label={`Descrição da data específica ${index + 1}`}
                              className={`${inputBase} min-h-[44px] min-w-0 max-w-full`}
                              value={item.label}
                              onChange={(e) => updateSpecialDate(index, 'label', e.target.value)}
                              placeholder="Ex.: Feriado municipal"
                            />
                            <label className="flex min-h-[44px] cursor-pointer items-center gap-2 whitespace-nowrap px-1 text-sm text-slate-700">
                              <input
                                type="checkbox"
                                checked={item.closed}
                                onChange={(e) => updateSpecialDate(index, 'closed', e.target.checked)}
                                className="h-4 w-4 accent-teal-600"
                              />
                              Fechado
                            </label>
                          </div>
                          {!item.closed && (
                            <div className="space-y-3">
                              {item.periods.length === 0 && (
                                <p className="text-xs text-slate-400">
                                  Sem períodos cadastrados.
                                </p>
                              )}
                              {item.periods.map((period, periodIndex) => (
                                <div
                                  key={`${item.date || 'special'}-${periodIndex}`}
                                  className="flex max-w-2xl flex-wrap items-center gap-2"
                                >
                                  <input
                                    type="time"
                                    aria-label={`Início do horário especial ${periodIndex + 1}`}
                                    className={`${inputBase} !w-32 flex-none min-h-[44px] text-base`}
                                    value={period.start}
                                    onChange={(e) => updateSpecialPeriod(index, periodIndex, 'start', e.target.value)}
                                  />
                                  <span className="text-sm text-slate-400" aria-hidden="true">até</span>
                                  <input
                                    type="time"
                                    aria-label={`Fim do horário especial ${periodIndex + 1}`}
                                    className={`${inputBase} !w-32 flex-none min-h-[44px] text-base`}
                                    value={period.end}
                                    onChange={(e) => updateSpecialPeriod(index, periodIndex, 'end', e.target.value)}
                                  />
                                  <button
                                    type="button"
                                    onClick={() => removeSpecialPeriod(index, periodIndex)}
                                    className="ml-auto min-h-[44px] whitespace-nowrap rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium text-slate-700 hover:border-rose-300 hover:text-rose-600"
                                  >
                                    Remover
                                  </button>
                                </div>
                              ))}
                              <button
                                type="button"
                                onClick={() => addSpecialPeriod(index)}
                                className="min-h-[44px] rounded-lg pr-3 text-sm font-semibold text-teal-700 hover:text-teal-800"
                              >
                                + Adicionar período
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={addSpecialDate}
                        className="min-h-[44px] rounded-lg pr-3 text-sm font-semibold text-teal-700 hover:text-teal-800"
                      >
                        + Adicionar data específica
                      </button>
                    </div>
                  </Field>
              </SectionCard>
            
)}
<SectionCard
                title="Pagamento"
                icon={CreditCard}
                description="A IA usa as formas de pagamento para responder dúvidas como “aceitam Pix?” e, quando registra pedidos, para fechar o pedido."
                hint={
                  modoPedidos === 'whatsapp'
                    ? 'Obrigatório: a IA registra pedidos pelo WhatsApp e precisa confirmar o pagamento com o cliente.'
                    : 'Preencha para a IA responder as dúvidas dos clientes sobre pagamento.'
                }
              >
                <h3 className="text-sm font-bold text-slate-900">Formas aceitas</h3>
                <Field
                  label="Formas de pagamento"
                  required={modoPedidos === 'whatsapp'}
                  error={errors.metodos_pagamento}
                  errorKey="metodos_pagamento"
                  tooltip="Selecione o que você realmente aceita no dia a dia."
                >
                  <div className="flex flex-wrap gap-2">
                    {PAYMENT_OPTIONS.map((option) => (
                      <Chip
                        key={option}
                        active={form.metodos_pagamento.includes(option)}
                        label={option}
                        onClick={() => toggleArrayValue('metodos_pagamento', option)}
                      />
                    ))}
                  </div>
                </Field>
                {(form.metodos_pagamento.includes('crédito') || form.metodos_pagamento.includes('débito')) && (
                <Field label="Bandeiras aceitas" errorKey="bandeiras_aceitas">
                  <div className="flex flex-wrap items-center gap-2">
                    {form.bandeiras_aceitas.map((item) => (
                      <span
                        key={item}
                        className="flex items-center gap-2 rounded-full border border-slate-200 bg-slate-100 px-3 py-1 text-xs text-slate-700"
                      >
                        {item}
                        <button
                          type="button"
                          onClick={() => removeBandeira(item)}
                          className="text-rose-500"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <input
                      className={`${inputBase} max-w-xs`}
                      aria-label="Adicionar bandeira de cartão"
                      value={bandeiraInput}
                      onChange={(e) => setBandeiraInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ',') {
                          e.preventDefault();
                          addBandeira();
                        }
                      }}
                      placeholder="Ex.: Visa, Master, Elo"
                    />
                    <button
                      type="button"
                      onClick={addBandeira}
                      className="rounded-lg border border-slate-200 bg-white px-4 text-xs font-semibold text-slate-700 hover:border-teal-400 hover:text-teal-700"
                    >
                      Adicionar
                    </button>
                  </div>
                </Field>
                )}
                <h3 className="text-sm font-bold text-slate-900">Regras de pagamento</h3>
                <div className="grid gap-4 md:grid-cols-3">
                  <Field label="Aceita dividir em dois cartões?" errorKey="pode_2_cartoes">
                    <BooleanToggle
                      value={form.pode_2_cartoes}
                      onChange={(value) => updateField('pode_2_cartoes', value)}
                    />
                  </Field>
                  <Field label="Aceita combinar Pix e cartão?" errorKey="pode_pix_cartao">
                    <BooleanToggle
                      value={form.pode_pix_cartao}
                      onChange={(value) => updateField('pode_pix_cartao', value)}
                    />
                  </Field>
                  {/* <Field
                    label="Preparar o pedido somente após o pagamento?"
                    errorKey="exige_pagamento_antecipado"
                    tooltip="Útil para encomendas e pedidos grandes."
                  >
                    <BooleanToggle
                      value={form.exige_pagamento_antecipado}
                      onChange={(value) => updateField('exige_pagamento_antecipado', value)}
                    />
                  </Field> */}
                </div>
                {form.metodos_pagamento.includes('pix') && (
                <Field
                  label="Chave Pix"
                  required={form.metodos_pagamento.includes('pix')}
                  error={errors.pix_chave}
                  errorKey="pix_chave"
                  tooltip="Obrigatório se Pix estiver selecionado."
                >
                  <input
                    className={inputBase}
                    value={form.pix_chave}
                    onChange={(e) => updateField('pix_chave', e.target.value)}
                    placeholder="Ex.: 51 99999-9999 ou chave aleatória"
                  />
                </Field>
                )}
              </SectionCard>
            
{(showLinks) && (
<SectionCard
                title="Canais de contato"
                icon={Phone}
                description="Canais que a IA pode informar ao cliente."
                hint="Preencha somente os canais usados pelo restaurante."
              >
                <div className="grid gap-4 md:grid-cols-2">
                  <Field label="Telefone para ligações" errorKey="telefone_ligacoes">
                    <input
                      className={inputBase}
                      value={form.telefone_ligacoes}
                      onChange={(e) => updateField('telefone_ligacoes', e.target.value)}
                    />
                  </Field>
                  <Field label="E-mail de atendimento" error={errors.email_atendimento} errorKey="email_atendimento">
                    <input
                      className={inputBase}
                      value={form.email_atendimento}
                      onChange={(e) => updateField('email_atendimento', e.target.value)}
                      placeholder="Ex.: contato@seuestabelecimento.com.br"
                    />
                  </Field>
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  <Field label="Site" error={errors.site} errorKey="site">
                    <input
                      className={inputBase}
                      value={form.site}
                      onChange={(e) => updateField('site', e.target.value)}
                      placeholder="Ex.: https://..."
                    />
                  </Field>
                </div>
                <button
                  type="button"
                  onClick={() => setShowLinksAdvanced((prev) => !prev)}
                  className="text-xs font-semibold text-teal-700 hover:text-teal-800"
                >
                  {showLinksAdvanced ? 'Ocultar canais adicionais' : 'Mostrar canais adicionais'}
                </button>
                {showLinksAdvanced && (
                  <div className="grid gap-4 md:grid-cols-2">
                    <Field label="Instagram" errorKey="instagram">
                      <input
                        className={inputBase}
                        value={form.instagram}
                        onChange={(e) => updateField('instagram', e.target.value)}
                        placeholder="Ex.: https://instagram.com/..."
                      />
                    </Field>
                    <Field label="Facebook" errorKey="facebook">
                      <input
                        className={inputBase}
                        value={form.facebook}
                        onChange={(e) => updateField('facebook', e.target.value)}
                        placeholder="Ex.: https://facebook.com/..."
                      />
                    </Field>
                    <Field label="TikTok" errorKey="tiktok">
                      <input
                        className={inputBase}
                        value={form.tiktok}
                        onChange={(e) => updateField('tiktok', e.target.value)}
                        placeholder="Ex.: https://tiktok.com/..."
                      />
                    </Field>
                    <Field label="Google Meu Negócio" errorKey="google_meu_negocio">
                      <input
                        className={inputBase}
                        value={form.google_meu_negocio}
                        onChange={(e) => updateField('google_meu_negocio', e.target.value)}
                        placeholder="Ex.: link do Google"
                      />
                    </Field>
                  </div>
                )}
              </SectionCard>
            
)}
<SectionCard
                title="Eventos e encomendas"
                icon={Package}
                description="Ative apenas os atendimentos extras que o restaurante realmente oferece."
              >
                <div className="grid gap-4 md:grid-cols-2">
                  <div className={`rounded-xl border p-4 space-y-3 ${eventosDirty ? 'border-amber-300 bg-amber-50/60' : eventosOn ? 'border-teal-300 bg-teal-50/40' : 'border-slate-200'}`}>
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                          Eventos
                          {eventosDirty && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-700">
                              Rascunho
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-slate-500">Ex.: aniversários, confraternizações, buffet sob consulta.</p>
                      </div>
                      <BooleanToggle
                        value={eventosOn}
                        onChange={(value) => setAtendimentoType('eventos', value)}
                      />
                    </div>
                    <p className="text-xs leading-5 text-slate-600">
                      {eventosOn
                        ? 'Ativado: a IA informa que o restaurante atende eventos e segue as regras que você escrever abaixo.'
                        : 'Desativado: a IA não menciona atendimento para eventos.'}
                    </p>
                  </div>
                  <div className={`rounded-xl border p-4 space-y-3 ${encomendasDirty ? 'border-amber-300 bg-amber-50/60' : encomendasOn ? 'border-teal-300 bg-teal-50/40' : 'border-slate-200'}`}>
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                          Encomendas
                          {encomendasDirty && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-700">
                              Rascunho
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-slate-500">Ex.: bolos, salgados e outros produtos por encomenda.</p>
                      </div>
                      <BooleanToggle
                        value={encomendasOn}
                        onChange={(value) => setAtendimentoType('encomendas', value)}
                      />
                    </div>
                    <p className="text-xs leading-5 text-slate-600">
                      {encomendasOn
                        ? 'Ativado: a IA informa que o restaurante aceita encomendas e segue as regras que você escrever abaixo.'
                        : 'Desativado: a IA não menciona atendimento para encomendas.'}
                    </p>
                  </div>
                </div>
                {(eventosOn || encomendasOn) && (
                  <Field
                    label="Regras especiais para eventos e encomendas"
                    required
                    error={errors.frases_ou_regras_especiais}
                    errorKey="frases_ou_regras_especiais"
                    helper="A IA usa exatamente este texto para orientar o cliente. Inclua prazos, orçamento e forma de contato."
                    tooltip="A IA segue essas regras ao pé da letra."
                  >
                    <textarea
                      className={`${inputBase} min-h-[120px]`}
                      value={form.frases_ou_regras_especiais}
                      onChange={(e) => updateField('frases_ou_regras_especiais', e.target.value)}
                      placeholder="Ex.: Encomendas com 24h de antecedência. Eventos sob consulta, orçamento pelo WhatsApp."
                    />
                  </Field>
                )}
                {encomendasOn && (
                  <p className="text-xs text-slate-500">
                    Encomendas também usa a entrega/retirada e as formas de pagamento definidas nas outras etapas.
                  </p>
                )}
              </SectionCard>
            
  </>
);

export default RestaurantStep;
